import { MultiFormStep, MultiFormStepper, TranslatedError } from '@if/mui'
import { Alert, Box, Button } from '@mui/material'
import { Breadcrumb } from '@src/components'
import {
  CalculatorSimulatePrequalificationResultDto,
  useCalculatorSimulatePrequalification,
} from '@src/data/api/calculator/calculator-api'
import { useMemo, useState, type JSX } from 'react'
import type { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CalculatorValues } from './calculatorSchema'
import CalculatorFields from './components/CalculatorFields'
import { useCalculatorCompute } from './hooks/useCalculatorCompute'
import { CalculatorFormSetup, useCalculatorFormFields, useCalculatorFormSetup } from './hooks/useCalculatorFormSetup'
import SimulatePrequalForm, {
  SIMULATE_PREQUAL_DEFAULT_VALUES,
  simulatePrequalSchema,
  SimulatePrequalFormValues,
} from './SimulatePrequalForm'

const StandaloneCalculatorPage = (): JSX.Element => {
  const { t } = useTranslation()
  const [prequalValues, setPrequalValues] = useState<SimulatePrequalFormValues | null>(null)
  const [simulateResult, setSimulateResult] = useState<CalculatorSimulatePrequalificationResultDto | null>(null)
  const simulatePrequalification = useCalculatorSimulatePrequalification()

  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '#', label: t('calculator.pageTitle') },
  ]

  const { today, expiresOn } = useMemo(() => {
    const today = new Date()
    const expiresOn = new Date(today)
    expiresOn.setDate(expiresOn.getDate() + 60)
    return { today, expiresOn }
  }, [])

  const createApplicationHref = useMemo(() => {
    if (!prequalValues || prequalValues.financingProgramId === '') return null
    const params = new URLSearchParams()
    if (prequalValues.merchantId) params.set('merchantId', prequalValues.merchantId)
    if (prequalValues.provinceIso !== '') params.set('stateIso', prequalValues.provinceIso)
    if (prequalValues.requestedLoanAmount != null) {
      params.set('requestedAmount', String(prequalValues.requestedLoanAmount))
    }
    const query = params.toString()
    return `/Applications/${prequalValues.financingProgramId}/add${query ? `?${query}` : ''}`
  }, [prequalValues])

  const setup = useCalculatorFormSetup({
    creditApplicationId: null,
    financingProgramId:
      prequalValues && prequalValues.financingProgramId !== '' ? prequalValues.financingProgramId : null,
    merchantId: prequalValues?.merchantId ?? null,
    expiresOn,
    createdOn: today,
    decisionInterestRate: simulateResult?.minInterestRate ?? null,
    maxAmountFinanced: simulateResult?.maxLoanAmount ?? null,
    maxTermDuration: simulateResult?.maxLoanTerm ?? null,
    hasCoApplicant: prequalValues?.hasCoApplicant ?? false,
    stateIso: prequalValues && prequalValues.provinceIso !== '' ? prequalValues.provinceIso : null,
    normsCreditDecision: null,
  })

  const {
    compute,
    result: computeResult,
    isComputing,
  } = useCalculatorCompute({
    config: setup.config,
    financingProgramId: setup.config?.financingProgramId ?? null,
    hasCoApplicant: prequalValues?.hasCoApplicant ?? false,
    stateIso: setup.config?.stateIso ?? null,
    creditApplicationId: null,
    merchantId: setup.merchant?.id,
    interestRate: setup.interestRate,
    insuranceCompanyId: setup.merchant?.insuranceProviderIds?.[0] ?? null,
  })

  // Throw to keep the stepper on this step (message shown in its alert); return true to advance.
  const handleSimulatePrequalNext = async ({ values }: { values: SimulatePrequalFormValues }) => {
    // Narrows the nullable inputs for the request DTO; the resolver already enforced required.
    if (values.ersScore == null || values.merchantId == null || values.requestedLoanAmount == null) {
      return false
    }
    setSimulateResult(null)

    let next: CalculatorSimulatePrequalificationResultDto
    try {
      next = await simulatePrequalification.mutateAsync({
        ersScore: values.ersScore,
        financingProgramId: values.financingProgramId,
        provinceIso: values.provinceIso,
        hasCoApplicant: values.hasCoApplicant,
        merchantId: values.merchantId,
        requestedLoanAmount: values.requestedLoanAmount,
        applicantAnnualIncome: values.applicantAnnualIncome,
        coApplicantAnnualIncome: values.hasCoApplicant ? values.coApplicantAnnualIncome : null,
      })
    } catch {
      throw new TranslatedError('calculator.simulationError')
    }

    setSimulateResult(next)
    if (next.decision !== 'approved') {
      throw new TranslatedError('calculator.simulationDeclined')
    }
    setPrequalValues(values)
    return true
  }

  const handleComplete = async (data: unknown[]) => {
    const calcValues = data[1] as CalculatorValues
    await compute(calcValues)
  }

  const useSimulatePrequalFormSetup = () => ({
    schema: simulatePrequalSchema,
    defaultValues: SIMULATE_PREQUAL_DEFAULT_VALUES,
  })

  // Mounts lazily under `ready={setup.config != null}`, so config is non-null here.
  const useCalculatorStepFormSetup = () => {
    const { schema, defaultValues } = useCalculatorFormFields({
      config: setup.config!,
      availablePromoCodes: setup.availablePromoCodes,
      worksheet: null,
      requestedLoanAmount:
        simulateResult?.maxLoanAmount != null && prequalValues?.requestedLoanAmount != null
          ? Math.min(simulateResult.maxLoanAmount, prequalValues.requestedLoanAmount)
          : (simulateResult?.maxLoanAmount ?? prequalValues?.requestedLoanAmount ?? null),
    })
    return { schema, defaultValues }
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Breadcrumb trees={breadCrumbs} />
      <MultiFormStepper
        orientation="vertical"
        onComplete={handleComplete}
        labels={{ submit: t('calculator.calculate') }}
      >
        <MultiFormStep<SimulatePrequalFormValues>
          label={t('calculator.steps.prequalification')}
          useFormSetup={useSimulatePrequalFormSetup}
          onNext={handleSimulatePrequalNext}
          labels={{ next: t('calculator.simulatePrequalification') }}
        >
          {(methods) => <SimulatePrequalForm methods={methods} />}
        </MultiFormStep>

        <MultiFormStep<CalculatorValues>
          label={t('calculator.steps.calculator')}
          ready={setup.config != null}
          useFormSetup={useCalculatorStepFormSetup}
          actions={
            createApplicationHref ? (
              <Button href={createApplicationHref} variant="outlined" color="primary">
                {t('calculator.createCreditApplication')}
              </Button>
            ) : null
          }
        >
          {(methods) => (
            <CalculatorStepContent
              control={methods.control}
              setup={setup}
              simulateResult={simulateResult}
              expiresOn={expiresOn}
              computeResult={computeResult}
              isComputing={isComputing}
            />
          )}
        </MultiFormStep>
      </MultiFormStepper>
    </Box>
  )
}

interface CalculatorStepContentProps {
  control: Control<CalculatorValues>
  setup: CalculatorFormSetup
  simulateResult: CalculatorSimulatePrequalificationResultDto | null
  expiresOn: Date
  computeResult: ReturnType<typeof useCalculatorCompute>['result']
  isComputing: boolean
}

const CalculatorStepContent = ({
  control,
  setup,
  simulateResult,
  expiresOn,
  computeResult,
  isComputing,
}: CalculatorStepContentProps) => {
  const { t } = useTranslation()

  return (
    <>
      {simulateResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('calculator.simulationDecision', {
            decision: simulateResult.decision,
            amount: simulateResult.maxLoanAmount,
          })}
        </Alert>
      )}
      <CalculatorFields
        control={control}
        config={setup.config!}
        result={computeResult}
        isComputing={isComputing}
        interestRate={setup.interestRate}
        expiresOn={expiresOn}
        isEstimate
        isStandalone
      />
    </>
  )
}

export default StandaloneCalculatorPage
